import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
import componentStyles from './idb-slider.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import Splide from '@splidejs/splide';

/**
 * The IDB Slider
 *
 * @element idb-slider
 * @slot - default slot for any slider content
 */

@customElement('idb-slider')
export class IdbSlider extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, attribute: 'slider-backgrounds' })
  sliderBackgrounds: '';

  @property({ type: Number, attribute: 'slider-speed' })
  sliderSpeed = 5000;

  @property({ type: Boolean, attribute: 'slider-arrows' })
  sliderArrows = false ;

  @query('.splide')
  splideEl: HTMLElement;

  splide: Splide;

  observer: MutationObserver;

  connectedCallback() {
    super.connectedCallback();
    this.observer = new MutationObserver(() => {
      this.requestUpdate();
    });
    this.observer.observe(this, { childList: true });
  }

  firstUpdated() {
    const splideTrack = this.shadowRoot?.querySelector(
      '.splide__track'
    ) as HTMLElement;
    const splideList = document.createElement('div');
    splideList.classList.add('splide__list');

    Array.from(this.children).forEach((item, idx, items) => {
      // remove item from lightdom right away to avoid browser repainting while processing
      splideList.appendChild(item);
      // process slide items
      item.classList.add('splide__slide', 'splide__slide__container');
      // item.setAttribute('role', 'group'); // probably it's better to move this to Drupal
      item.setAttribute('aria-label', `slide ${idx + 1} of ${items.length}`); // probably it's better to move this to Drupal

      // Set slide background according to the slider-backgrounds attribute
      const sliderBg = this.sliderBackgrounds.split(',');
      item.setAttribute('background', `${sliderBg[idx].replace(/\s/g, '')}`);
    });
    console.log(this.sliderArrows);
    // append our elements to our shadowdom.
    splideTrack.appendChild(splideList);
    this.splide = new Splide(this.splideEl, {
      start: 0,
      keyboard: true,
      autoplay: true,
      speed: 500,
      interval: this.sliderSpeed ? this.sliderSpeed : 5000,
      reducedMotion: true,
      arrows: this.sliderArrows ? this.sliderArrows : false,
      arrowPath: 'M5 12 H19 M12 5 L19 12 L12 19',
      height: '100%',
      padding: { left: '0', right: '0' },
      perPage: 1,
      rewindByDrag: true,
      drag: true,
      updateOnMove: true,
      snap: true,
      gap: '2rem',
      type: 'fade',
      rewind: true,
    }).mount();
  }

  updated() {
    setTimeout(() => {
      this.setHiddenAttrOnSlides();
      this.setHTMLVideoHandling();
    }, 1000);
  }

  disconnectedCallback() {
    this.observer.disconnect();
    this.splide.destroy();
  }

  render(): TemplateResult {
    return html`
      <div class="slider">
        <div class="splide">
          <div class="splide__track"></div>
        </div>
      </div>
    `;
  }

  setHiddenAttrOnSlides = () => {
    const hiddenSlides: NodeListOf<HTMLElement> | undefined =
      this.shadowRoot?.querySelectorAll('.splide__slide[aria-hidden="true"]');

    // Sets the not-visible attribute/prop on the card when hidden.
    // On the card component, the tabindex value is controlled by this.
    if (hiddenSlides?.length) {
      Array.from(hiddenSlides).map(e => {
        e.querySelectorAll('a').forEach(e => e.setAttribute('tabindex', '-1'));
      });
    }
  };

  setHTMLVideoHandling(){
    const videoSlides: NodeListOf<HTMLElement> | undefined =
      this.shadowRoot?.querySelectorAll('idb-video-card');

    if (!videoSlides?.length) {
      return;
    }

    const pauseVideos = (firstCall: boolean): void => {
      if (videoSlides?.length) {
        Array.from(videoSlides).map((video, idx) => {
          const videoWrapper: HTMLVideoElement | undefined | null = video.shadowRoot?.querySelector('video');
          if (videoWrapper && videoWrapper.hasAttribute('autoplay')) {
            if (firstCall && idx !== 0) {
              videoWrapper.pause();
            } else if (!firstCall) {
              videoWrapper.pause();
            }
          }
        });
      }
    }

    const playCurrentVideo = (): void=> {
      const activeSlide = this.splide.Components.Elements.slides[this.splide.index];
      const videoCard = activeSlide?.querySelector('idb-video-card') as HTMLElement | null;
      const videoCardWrapper: HTMLVideoElement | undefined | null = videoCard?.shadowRoot?.querySelector('video');

      if (videoCardWrapper  && videoCardWrapper.hasAttribute('autoplay')) {
        videoCardWrapper.play().catch(console.error);
      }
    }

    pauseVideos(true);

    this.splide.on('moved', () => {
      pauseVideos(false);
      playCurrentVideo();
    });
  }

}
